import { memo, useCallback, useRef, useState } from 'react';
import { Platform, Pressable, View, type ViewStyle } from 'react-native';
import ReanimatedSwipeable from 'react-native-gesture-handler/ReanimatedSwipeable';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { CoachNotification } from '@/features/coach/notifications/types';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

const strings = STRINGS.coach.notifications;

/** Keep the delete layer above the sliding row on web (same fix as Guardian notifications). */
const WEB_STABILIZE_DELETE_LAYER = (Platform.select({
  web: {
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    willChange: 'transform',
  },
  default: {},
}) ?? {}) as ViewStyle;

type CoachNotificationItemProps = {
  item: CoachNotification;
  onPress: (item: CoachNotification) => void;
  onDelete: (id: string) => void;
};

/**
 * One notification row. Unread shows the blue type label, bold title and a red dot; read is
 * dimmed with no dot. Swiping right→left reveals the delete action (BR-02: no confirmation).
 */
export const CoachNotificationItem = memo(function CoachNotificationItem({
  item,
  onPress,
  onDelete,
}: CoachNotificationItemProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const [isSwipeOpen, setIsSwipeOpen] = useState(false);
  // A ref flips synchronously, unlike state: the finger-up that finishes a horizontal swipe can
  // otherwise fire `onPress` and navigate away instead of just revealing the delete action.
  const suppressPressRef = useRef(false);

  const handleSwipeStart = useCallback(() => {
    suppressPressRef.current = true;
    setIsSwipeOpen(true);
  }, []);

  const handleSwipeClose = useCallback(() => {
    setIsSwipeOpen(false);
    setTimeout(() => {
      suppressPressRef.current = false;
    }, 100);
  }, []);

  const handleItemPress = useCallback(() => {
    if (suppressPressRef.current || isSwipeOpen) return;
    onPress(item);
  }, [isSwipeOpen, item, onPress]);

  const renderDeleteAction = useCallback(
    () => (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={strings.accessibility.delete(item.title)}
        onPress={() => onDelete(item.id)}
        style={[styles.deleteAction, WEB_STABILIZE_DELETE_LAYER]}>
        <SvgIcon name="IcNotificationTrash" size={scale(20)} color={colors['0']} />
      </Pressable>
    ),
    [item.id, item.title, onDelete, scale, styles.deleteAction],
  );

  return (
    <View
      style={[
        styles.shadowContainer,
        item.isRead ? styles.shadowContainerRead : styles.itemUnread,
      ]}>
      <View style={styles.swipeContainer}>
        <ReanimatedSwipeable
          friction={2}
          onSwipeableOpenStartDrag={handleSwipeStart}
          onSwipeableCloseStartDrag={handleSwipeStart}
          onSwipeableClose={handleSwipeClose}
          onSwipeableWillOpen={() => setIsSwipeOpen(true)}
          overshootRight={false}
          rightThreshold={scale(36)}
          renderRightActions={renderDeleteAction}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={item.title}
            onPress={handleItemPress}
            style={({ pressed }) => [
              styles.item,
              !isSwipeOpen ? styles.itemRounded : null,
              item.isRead
                ? isSwipeOpen
                  ? // Opaque screen-bg while swiping so timestamp doesn't paint over Delete
                    // (transparent would look correct but composites over the red action on web).
                    styles.itemReadSwipe
                  : styles.itemRead
                : styles.itemUnread,
              pressed ? styles.pressed : null,
            ]}>
            <View style={styles.itemIcon}>
              <SvgIcon name={item.icon} size={scale(24)} color={colors.Blue} />
            </View>

            <Stack flex={1} gap={scale(2)}>
              {/* Type label sits on the same row as the timestamp + unread dot. */}
              <Stack direction="row" align="flex-start" gap={scale(8)}>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
                  numberOfLines={1}
                  style={[styles.typeLabel, item.isRead ? styles.dimmedLabel : null]}>
                  {item.typeLabel}
                </Text>
                <Stack flex={1} />
                <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.time}>
                  {item.timeLabel}
                </Text>
                {!item.isRead ? <View style={styles.unreadDot} /> : null}
              </Stack>

              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                style={[styles.title, item.isRead ? styles.dimmed : null]}>
                {item.title}
              </Text>
              {item.subtitle ? (
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                  style={[styles.subtitle, item.isRead ? styles.dimmed : null]}>
                  {item.subtitle}
                </Text>
              ) : null}
            </Stack>
          </Pressable>
        </ReanimatedSwipeable>
      </View>
    </View>
  );
});

const useStyles = createStyles(({ scale }) => ({
  shadowContainer: {
    borderRadius: scale(2),
    shadowColor: '#D5D9E2',
    shadowOffset: { width: 0, height: scale(1) },
    shadowOpacity: 0.3,
    shadowRadius: scale(1.5),
    elevation: 2,
  },
  swipeContainer: {
    borderRadius: scale(2),
    overflow: 'hidden',
  },
  item: {
    minHeight: scale(72),
    flexDirection: 'row',
    // Icon sits level with the first text row, not centred against the whole block.
    alignItems: 'flex-start',
    gap: scale(12),
    padding: scale(12),
    backgroundColor: colors['0'],
  },
  itemRounded: {
    borderRadius: scale(2),
    marginRight: Platform.OS === 'web' ? 1 : 0,
  },
  // Unread rows stay white so they stand out; read rows are transparent and let the screen
  // background show through.
  shadowContainerRead: {
    backgroundColor: 'transparent',
    shadowOpacity: 0,
    elevation: 0,
  },
  itemUnread: { backgroundColor: colors['0'] },
  itemRead: { backgroundColor: 'transparent' },
  // Match screen bg (`colors['1']`) so swipe doesn't flash white on a read row.
  itemReadSwipe: { backgroundColor: colors['1'] },
  itemIcon: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(8),
    backgroundColor: '#EEF3FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  typeLabel: { color: colors.Blue },
  dimmedLabel: { color: colors['6'] },
  title: { color: colors['10'] },
  subtitle: { color: colors['6'] },
  dimmed: { color: colors['6'] },
  unreadDot: {
    width: scale(8),
    height: scale(8),
    borderRadius: scale(4),
    backgroundColor: colors.Red,
  },
  time: { color: colors['6'] },
  deleteAction: {
    width: scale(60),
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.Error,
    zIndex: 1,
  },
  pressed: { opacity: Platform.OS === 'web' ? 1 : 0.72 },
}));

export default CoachNotificationItem;
